<script lang="ts">
  interface Props {
    borderType?: 'shadow' | 'fog' | 'solid' | 'shine';
    children?: import('svelte').Snippet;
  }

  let { borderType = 'solid', children }: Props = $props();

  // Steel ramp, darkest to brightest.
  let metalDeep = '#50504c';
  let metalDark = '#60605a';
  let metalMid = '#71716b';
  let metalBase = '#888880';
  let metalLight = '#98988f';
  let metalPale = '#b6b6a9';
  let metalShine = '#d4d4b6';

  // One clamp shape, mirrored into each corner, so all four match.
  const clamp =
    'M12 3 L108 3 Q117 3 117 12 L99 36 L55 36 Q36 36 36 55 L36 99 L12 117 Q3 117 3 108 L3 12 Q3 3 12 3 Z';

  const corners = [
    { key: 'TL', top: true, left: true },
    { key: 'TR', top: true, left: false },
    { key: 'BL', top: false, left: true },
    { key: 'BR', top: false, left: false },
  ];

  // The light sits off the top right of the screen. Because each corner is the
  // same path mirrored into place, every gradient has to be pre-flipped to
  // cancel that mirror out, otherwise the lighting flips along with the shape.
  // Lit end of the ramp is always listed first.
  const faceDirs = [
    { key: 'TL', x1: 1, y1: 0, x2: 0, y2: 1 },
    { key: 'TR', x1: 0, y1: 0, x2: 1, y2: 1 },
    { key: 'BL', x1: 1, y1: 1, x2: 0, y2: 0 },
    { key: 'BR', x1: 0, y1: 1, x2: 1, y2: 0 },
  ];

  // Bevel across an arm's thickness: 3 is the arm's outer edge in local
  // coordinates, 36 its inner edge. A horizontal arm is lit along whichever
  // edge faces up, a vertical arm along whichever edge faces right, which is
  // what makes the top right clamp fully lit and the bottom left one lit only
  // on its inside edges.
  const edgeDirs = [
    { key: 'Top', x1: 0, y1: 3, x2: 0, y2: 36 },
    { key: 'Bottom', x1: 0, y1: 36, x2: 0, y2: 3 },
    { key: 'Right', x1: 3, y1: 0, x2: 36, y2: 0 },
    { key: 'Left', x1: 36, y1: 0, x2: 3, y2: 0 },
  ];

  const rivetDirs = [
    { key: 'TL', cx: '65%', cy: '30%' },
    { key: 'TR', cx: '35%', cy: '30%' },
    { key: 'BL', cx: '65%', cy: '70%' },
    { key: 'BR', cx: '35%', cy: '70%' },
  ];
</script>

<div class="frame {borderType}">
  <svg class="clamp-defs" aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg">
    <defs>
      {#each faceDirs as dir}
        <linearGradient id="clampFace{dir.key}" x1={dir.x1} y1={dir.y1} x2={dir.x2} y2={dir.y2}>
          <stop offset="0%" stop-color={metalBase} />
          <stop offset="9%" stop-color={metalPale} />
          <stop offset="16%" stop-color={metalShine} />
          <stop offset="23%" stop-color={metalLight} />
          <stop offset="42%" stop-color={metalMid} />
          <stop offset="70%" stop-color={metalDark} />
          <stop offset="100%" stop-color={metalDeep} />
        </linearGradient>
      {/each}

      {#each edgeDirs as dir}
        <linearGradient
          id="clampEdge{dir.key}"
          gradientUnits="userSpaceOnUse"
          x1={dir.x1}
          y1={dir.y1}
          x2={dir.x2}
          y2={dir.y2}
        >
          <stop offset="0%" stop-color="#fffff4" stop-opacity="0.55" />
          <stop offset="18%" stop-color="#fffff4" stop-opacity="0" />
          <stop offset="70%" stop-color="#000000" stop-opacity="0" />
          <stop offset="100%" stop-color="#000000" stop-opacity="0.38" />
        </linearGradient>
      {/each}

      {#each rivetDirs as dir}
        <radialGradient id="clampRivet{dir.key}" cx={dir.cx} cy={dir.cy} r="75%">
          <stop offset="0%" stop-color={metalShine} />
          <stop offset="45%" stop-color={metalBase} />
          <stop offset="100%" stop-color="#3b3b37" />
        </radialGradient>
      {/each}

      <clipPath id="clampClip">
        <path d={clamp} />
      </clipPath>
      <!-- Split on the 45 degree diagonal so the two arms meet in a mitred seam
           and each can take its own bevel. -->
      <clipPath id="clampArmH">
        <polygon points="0,0 120,0 120,120" />
      </clipPath>
      <clipPath id="clampArmV">
        <polygon points="0,0 0,120 120,120" />
      </clipPath>
    </defs>
  </svg>

  {#each corners as corner}
    <svg
      class="corner"
      class:top={corner.top}
      class:bottom={!corner.top}
      class:left={corner.left}
      class:right={!corner.left}
      viewBox="0 0 120 120"
      version="1.1"
      aria-hidden="true"
      focusable="false"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d={clamp}
        fill="url(#clampFace{corner.key})"
        stroke="rgba(0,0,0,0.48)"
        stroke-width="5"
        stroke-linejoin="round"
      />
      <g clip-path="url(#clampClip)">
        <g clip-path="url(#clampArmH)">
          <rect
            x="0"
            y="0"
            width="120"
            height="120"
            fill="url(#clampEdge{corner.top ? 'Top' : 'Bottom'})"
          />
        </g>
        <g clip-path="url(#clampArmV)">
          <rect
            x="0"
            y="0"
            width="120"
            height="120"
            fill="url(#clampEdge{corner.left ? 'Left' : 'Right'})"
          />
        </g>
      </g>
      <path
        d={clamp}
        fill="none"
        stroke="rgba(0,0,0,0.28)"
        stroke-width="1.5"
        stroke-linejoin="round"
      />

      <g class="rivets" fill="url(#clampRivet{corner.key})">
        <circle cx="19.5" cy="19.5" r="6.2" />
        <circle cx="91" cy="19.5" r="5.2" />
        <circle cx="19.5" cy="91" r="5.2" />
      </g>
    </svg>
  {/each}

  {@render children?.()}
</div>

<style lang="less">
  @import '../../defines.less';

  .frame {
    background: radial-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.9));
    padding: 5px;
    border-width: 4px;
    border-style: solid;
    border-image: linear-gradient(
        to top right,
        #2f1606 0%,
        #692f1f 20%,
        #692f1f 50%,
        #87442b 80%,
        #e8d7ae 90%,
        #87442b 100%
      )
      1;

    width: var(--width, calc(90vw - 80px));
    max-width: var(--max-width, 800px);
    margin: auto;
    // backdrop-filter: blur(5px);
    box-shadow: 0 0 5px 2px rgba(0, 0, 0, 0.5);
    position: relative;
    padding: 40px;

    @media @small {
      padding: 20px;
      width: var(--width, calc(90vw - 40px));
    }

    .clamp-defs {
      position: absolute;
      width: 0;
      height: 0;
      overflow: hidden;
    }

    .corner {
      position: absolute;
      width: 60px;
      height: 60px;
      pointer-events: none;
      filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.45));

      @media @small {
        width: 40px;
        height: 40px;
      }

      .rivets circle {
        stroke: rgba(0, 0, 0, 0.48);
        stroke-width: 1.5;
      }
    }

    // Mirror the one shape into each corner. The gradients are pre-flipped in
    // the markup to cancel this out, so the light stays in the top right.
    .top {
      top: -6px;
    }
    .left {
      left: -6px;
    }
    .right {
      right: -6px;
      transform: scaleX(-1);
    }
    .bottom {
      bottom: -6px;
      transform: scaleY(-1);
    }
    .bottom.right {
      transform: scale(-1, -1);
    }
  }
</style>
